import { Button, Dropdown, message, Modal, Table } from "antd";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthData } from "../../../Components/Global";
import {
    CheckCircleOutlined,
    DownOutlined,
    EditOutlined,
    ExclamationCircleFilled,
    PlusOutlined,
    SaveOutlined,
} from "@ant-design/icons";
import ImportExcelOrder from "./ImportExcel";
import FilterOrder from "./FilterOrder";
import { GetSPOrderDetail } from "../../../API/GetData";
import { PostOrder } from "../../../API/PostData";
import { encrypt } from "../../../Utils/CryptoHelper";

const Order = () => {
    const Auth = AuthData();
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [updateData, setUpdateData] = useState(false);
    const [importData, setImportData] = useState([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);

    const showModal = (record) => {
        setCurrentRecord(record);
        setIsModalOpen(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setModalLoading(false); // Reset loading saat modal ditutup
    };

    const handleClearStorage = () => {
        localStorage.removeItem("DataOrder");
        localStorage.removeItem("DataSample");
    };

    const handleSave = async (AccountId, OrderCode) => {
        if (currentRecord?.SalesName === null) {
            message.error("Sales Name NULL!");
            return;
        }
        if (AccountId && OrderCode) {
            setModalLoading(true);
            try {
                const response = await GetSPOrderDetail(AccountId, OrderCode);
                const dataOrder = response.Order;
                const dataSample = response.Sample.map((sample, index) => ({
                    ...sample,
                    key: index + 1,
                    Parameter: response.Parameter.filter(
                        (item) => item.SampleCode === sample.SampleCode
                    ).map((params, index) => ({
                        ...params,
                        key: index + 1,
                    })),
                }));

                const payload = {
                    ...dataOrder,
                    Sample: dataSample,
                };

                const resPost = await PostOrder(payload);

                if (resPost.status === 201 || resPost.status === 200) {
                    message.success(resPost.data.message);
                    navigate("/dashboard/menuorder/order");
                    setUpdateData(true);
                    setIsModalOpen(false);
                }
            } catch (error) {
                message.error(error.response?.data?.error || "Failed to save data");
                console.error("Error fetching data:", error);
            } finally {
                setModalLoading(false);
            }
        }
    };

    const handleEdit = async (AccountId, OrderCode) => {
        if (AccountId && OrderCode) {
            try {
                const response = await GetSPOrderDetail(AccountId, OrderCode);
                const dataOrder = response.Order;
                const dataSample = response.Sample.map((sample, index) => ({
                    ...sample,
                    key: index + 1,
                    Parameter: response.Parameter.filter(
                        (item) => item.SampleCode === sample.SampleCode
                    ).map((params, index) => ({
                        ...params,
                        key: index + 1,
                    })),
                }));

                localStorage.setItem("DataOrder", JSON.stringify(dataOrder));
                localStorage.setItem("DataSample", JSON.stringify(dataSample));
                navigate("/dashboard/menuorder/order/form");
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        }
    };

    const columns = [
        {
            key: "key",
            title: "#",
            dataIndex: "key",
            fixed: "left",
            sorter: (a, b) => a.key - b.key,
        },
        {
            key: "OrderQuotation",
            title: "Order Quotation",
            dataIndex: "OrderQuotation",
            sorter: (a, b) => a.OrderQuotation.localeCompare(b.OrderQuotation),
            fixed: "left",
            render: (value, record) => (
                <Link
                    to={`${encodeURIComponent(encrypt(record.AccountId))}/${encodeURIComponent(
                        encrypt(record.OrderCode)
                    )}`}
                    className="text-second hover:text-second-600 font-medium"
                >
                    {value}
                </Link>
            ),
        },
        {
            key: "OrderDate",
            title: "Order Date",
            dataIndex: "OrderDate",
            sorter: (a, b) => a.OrderDate.localeCompare(b.OrderDate),
        },
        {
            key: "CustomerName",
            title: "Customer Name",
            dataIndex: "CustomerName",
            sorter: (a, b) => a.CustomerName.localeCompare(b.CustomerName),
        },
        {
            key: "SalesName",
            title: "Sales Name",
            dataIndex: "SalesName",
            sorter: (a, b) => {
                const nameA = a.SalesName || "";
                const nameB = b.SalesName || "";
                return nameA.localeCompare(nameB);
            },
            render: (value) => (value ? value : <p>-</p>),
        },
        {
            key: "Department",
            title: "Department",
            dataIndex: "Department",
            sorter: (a, b) => a.Department.localeCompare(b.Department),
        },
    ];

    if (Auth.Role === "Admin") {
        columns.push({
            key: "Action",
            title: "Action",
            dataIndex: "Action",
            fixed: "right",
            render: (_, record) => {
                if (record.IsStatus)
                    return (
                        <div className="text-second text-center text-lg">
                            <CheckCircleOutlined />
                        </div>
                    );

                const items = [
                    {
                        key: "1",
                        label: (
                            <button
                                className="flex gap-2 hover:text-primary"
                                onClick={() => showModal(record)}
                            >
                                <SaveOutlined /> Publish
                            </button>
                        ),
                    },
                    {
                        key: "2",
                        label: (
                            <button
                                className="flex gap-2 hover:text-second"
                                onClick={() =>
                                    handleEdit(record.AccountId, record.OrderCode)
                                }
                            >
                                <EditOutlined /> Edit
                            </button>
                        ),
                    },
                ];

                return (
                    <div className="text-second hover:text-second-600 font-medium">
                        <Dropdown menu={{ items }} trigger={["click"]}>
                            <button>
                                More <DownOutlined />
                            </button>
                        </Dropdown>

                        {/* Modal Confirmation Saving Data */}
                        <Modal
                            centered
                            open={isModalOpen}
                            closable={false}
                            confirmLoading={modalLoading}
                            footer={[
                                <Button
                                    key="submit"
                                    type="primary"
                                    loading={modalLoading}
                                    onClick={() =>
                                        handleSave(currentRecord.AccountId, currentRecord.OrderCode)
                                    }>
                                    Submit
                                </Button>,
                                <Button key="back" disabled={modalLoading} onClick={handleCancel}>
                                    Cancel
                                </Button>,
                            ]}
                        >
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <ExclamationCircleFilled className="text-2xl text-primary" />
                                    <h1 className="font-bold text-xl">
                                        Are you sure{" "}
                                        <span className="text-green-500 font-blod">
                                            {" "}
                                            Save and Publish{" "}
                                        </span>{" "}
                                        this data?
                                    </h1>
                                </div>
                                <p className="font-medium">
                                    Order Quotation :{" "}
                                    <span className="text-second">
                                        {currentRecord?.OrderQuotation}
                                    </span>
                                </p>
                            </div>
                        </Modal>
                    </div>
                );
            },
        });
    }

    return (
        <div className="bg-white p-4 rounded-md">
            <FilterOrder
                dataSource={(values) => setData(values)}
                isLoading={(values) => setIsLoading(values)}
                importData={importData}
                updateData={updateData}
                onUpdateData={(value) => setUpdateData(value)}
            />

            {Auth && Auth.Role === "Admin" && (
                <div className="flex justify-end gap-2 my-4">
                    <ImportExcelOrder onImportData={(values) => setImportData(values)} />
                    <Link to={"form"}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleClearStorage}>
                            Add New
                        </Button>
                    </Link>
                </div>
            )}

            <Table
                columns={columns}
                dataSource={data}
                loading={isLoading}
                scroll={{
                    x: "max-content",
                }}
                pagination={{
                    showTotal: (total) => `Total ${total} data`,
                }}
            />
        </div>
    );
};

export default Order;
